import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { GoogleSignInButton, AuthOrDivider, GOOGLE_ENABLED } from '@/auth/GoogleSignInButton';
import './Login.css';

interface LocationState {
  from?: { pathname?: string };
}

export function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
  // Post-login return target for full-page entry points outside the SPA router (e.g.
  // the static /brewwcode-authorize.html editor-SSO consent page). Open-redirect guard:
  // only a same-origin absolute path (starts with a single '/', not '//') is honored.
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const safeRedirect = redirectParam && /^\/(?!\/)/.test(redirectParam) ? redirectParam : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    // 1. Add the external script tag
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-NN39PYE0R5';
    document.head.appendChild(script1);

    // 2. Add the inline script block
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-NN39PYE0R5');
    `;
    document.head.appendChild(script2);

    return () => {
      // Clean up scripts if we navigate away to keep the DOM clean
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  async function handleGoogleCredential(credential: string): Promise<void> {
    setSubmitting(true);
    setLocalError(null);
    try {
      const { waitlisted } = await auth.loginWithGoogle(credential);
      if (waitlisted) {
        setLocalError(
          "You're on the waiting list. Your account isn't open yet — we'll let you in as soon as a seat frees up.",
        );
        return;
      }
      if (safeRedirect) { window.location.assign(safeRedirect); return; }
      navigate(from, { replace: true });
    } catch (err) {
      const e = err as { status?: number; message?: string; body?: { error?: string; detail?: string } };
      if (e.status === 403 && e.body?.error === 'waitlisted') {
        setLocalError(
          "You're on the waiting list. Your account isn't open yet — we'll let you in as soon as a seat frees up.",
        );
      } else {
        // Prefer the server-provided detail (e.g. the real cause of a 500).
        setLocalError(e.body?.detail || e.message || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!email || !password) {
      setLocalError('Email and password are required.');
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      await auth.login(email, password);
      // Full-page return (static page outside the router) takes precedence over the
      // in-app `from` so the editor-SSO consent page resumes with a fresh session.
      if (safeRedirect) { window.location.assign(safeRedirect); return; }
      navigate(from, { replace: true });
    } catch (err) {
      const e = err as { status?: number; message?: string; body?: { error?: string } };
      if (e.status === 401) {
        setLocalError('Invalid email or password.');
      } else if (e.status === 403 && e.body?.error === 'waitlisted') {
        // Credentials were right — the account is still on the signup waitlist.
        setLocalError(
          "You're on the waiting list. Your account isn't open yet — we'll let you in as soon as a seat frees up.",
        );
      } else {
        setLocalError(e.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <header className="login__header">
          <img className="login__logo" src="/brew-logo.png" alt="Breww.ai" width={108} height={108} />
        </header>
        <form onSubmit={handleSubmit} className="login__form" noValidate>
          <label className="login__field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="login__field">
            <span>Password</span>
            <div className="login__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="login__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68"/><path d="M6.06 6.06C3.4 7.7 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 3.5-.64"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>
                )}
              </button>
            </div>
          </label>
          {localError && <div className="login__error">{localError}</div>}
          <button type="submit" className="login__submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {GOOGLE_ENABLED && (
          <>
            <AuthOrDivider />
            <GoogleSignInButton onCredential={handleGoogleCredential} text="continue_with" />
          </>
        )}
        <footer className="login__footer">
          <Link className="login__link" to="/forgot-password">Forgot password?</Link>
          <span className="login__sep" aria-hidden="true">·</span>
          <Link className="login__link login__link--accent" to="/register">Create account</Link>
        </footer>
      </div>
    </div>
  );
}
